import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowRight, FaBrain, FaHeart, FaLeaf, FaUsers, FaMoon, FaCheckCircle } from "react-icons/fa";
import { GiLotus, GiMeditation } from "react-icons/gi";
import { FiZap } from "react-icons/fi";

const gpu = {
  willChange: "transform, opacity",
  transform: "translateZ(0)",
  backfaceVisibility: "hidden",
};

const vp = (amount = 0.12) => ({ once: true, amount });

const fadeUp = {
  hidden: { opacity: 0, y: 44 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
  },
};


const slideLeft = {
  hidden: { opacity: 0, x: -70 },
  show: {
    opacity: 1, x: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

const slideRight = {
  hidden: { opacity: 0, x: 70 },
  show: {
    opacity: 1, x: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

const scaleUp = {
  hidden: { opacity: 0, scale: 0.9 },
  show: {
    opacity: 1, scale: 1,
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
  },
};

const listItem = {
  hidden: { opacity: 0, x: -20 },
  show: {
    opacity: 1, x: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

// Stagger container   keeps stagger tight so no visible cascade delay
const stagger = (d = 0.07) => ({
  hidden: {},
  show: { transition: { staggerChildren: d, delayChildren: 0.04 } },
});

// ── DATA ──────────────────────────────────────────────────────────────────────
const benefitsCards = [
  {
    title: "Physical Transformation",
    subtitle: "Strength • Flexibility • Vitality",
    desc: "Restore your body's natural balance through guided movement, posture correction, flexibility training, and mindful breathing practices.",
    image: "https://images.pexels.com/photos/3822622/pexels-photo-3822622.jpeg",
    icon: <FaHeart />,
    stats: "1000+ Students Improved Mobility",
  },
  {
    title: "Mental Clarity",
    subtitle: "Focus • Calmness • Mindfulness",
    desc: "Reduce stress, overthinking, and anxiety through breathing techniques, meditation, and restorative yoga experiences.",
    image: "https://images.pexels.com/photos/4325465/pexels-photo-4325465.jpeg",
    icon: <FaBrain />,
    stats: "98% Report Better Mental Wellness",
  },
  {
    title: "Spiritual Growth",
    subtitle: "Awareness • Peace • Discipline",
    desc: "Reconnect with yourself through authentic yogic wisdom, mindful awareness, and spiritual practices rooted in tradition.",
    image: "https://images.pexels.com/photos/8172935/pexels-photo-8172935.jpeg",
    icon: <FaLeaf />,
    stats: "Daily Meditation & Yogic Practices",
  },
  {
    title: "Lifestyle Discipline",
    subtitle: "Sleep • Energy • Balance",
    desc: "Create healthier routines, better sleep cycles, emotional balance, and sustainable wellness habits for everyday life.",
    image: "https://images.pexels.com/photos/4498606/pexels-photo-4498606.jpeg",
    icon: <FaMoon />,
    stats: "Holistic Daily Wellness Routines",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
const Benefits = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(true);

  const handleStartJourney = () => {
    const token = localStorage.getItem("divyayoga_token");
    const role = localStorage.getItem("divyayoga_role");
    if (token) {
      navigate(role === "admin" ? "/admin/dashboard" : "/user/dashboard");
    } else {
      navigate("/register");
    }
  };
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Parallax   runs on compositor thread via CSS custom property
  const heroRef = useRef(null);
  const { scrollYProgress: heroScroll } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  // Only transform   never triggers layout
  const heroY       = useTransform(heroScroll, [0, 1], ["0%", "15%"]);
  const heroOpacity = useTransform(heroScroll, [0, 0.65], [1, 0]);

  return (
    <div className="relative overflow-hidden bg-[#FFF9F2]">

      {/* ══════════════════════════════════════════════════════
          SECTION 1   HERO
      ══════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden">

        {/* Video / Background parallax wrapper */}
        <motion.div
          style={{ y: heroY, ...gpu }}
          className="absolute inset-0 w-full h-full"
        >
          {!isMobile ? (
            <video
              autoPlay muted loop playsInline preload="metadata"
              className="absolute inset-0 w-full h-full object-cover scale-[1.05]"
              style={{ willChange: "transform", transform: "translateZ(0)", backfaceVisibility: "hidden" }}
            >
              <source src="/assets/video18.mp4" type="video/mp4" />
            </video>
          ) : (
            <div className="absolute inset-0 w-full h-full">
              <div 
                className="absolute inset-0 w-full h-full bg-cover bg-center" 
                style={{ backgroundImage:"url('/assets/benefits_mobile_hero_bg.jpg')" }} 
              />
              {/* Subtle dark brown overlay on mobile background image */}
              <div className="absolute inset-0 bg-[#120700]/40 pointer-events-none" />
            </div>
          )}
        </motion.div>

        {/* Overlays: preserved original for desktop, balanced dark brown warmth for mobile */}
        {!isMobile ? (
          <>
            <div className="absolute inset-0 bg-[#120700]/60" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#F29A0C]/20 via-[#120700]/45 to-[#120700]/65" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.10),transparent_30%)]" />
          </>
        ) : (
          <>
            <div className="absolute inset-0 bg-[#120700]/20 pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#120700]/70 via-[#180902]/45 to-[#0A0400]/75 pointer-events-none" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(242,154,12,0.22)_0%,transparent_60%)] pointer-events-none" />
          </>
        )}

        {/* Glow orb */}
        <motion.div
          animate={{ opacity: [0.12, 0.22, 0.12] }}
          transition={{ repeat: Infinity, duration: 10, ease: "easeInOut" }}
          className="absolute top-[-240px] right-[-180px] w-[850px] h-[850px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.18)_0%,transparent_70%)] pointer-events-none will-change-opacity"
          style={gpu}
        />

        {/* Hero content fades out as page scrolls */}
        <motion.div
          style={{ opacity: heroOpacity, ...gpu }}
          className="relative z-20 min-h-screen flex py-4 items-center"
        >
          <div className="max-w-[1650px] mx-auto px-5 sm:px-6 py-4 lg:px-20 w-full">
            <motion.div
              className="max-w-[900px] pt-16 lg:pt-[120px]"
              variants={stagger(0.12)}
              initial="hidden"
              animate="show"
            >
              <motion.div
                variants={fadeUp}
                style={gpu}
                className="inline-flex items-center gap-2.5 sm:gap-3 rounded-full border border-white/20 bg-white/10 backdrop-blur-2xl px-4 sm:px-6 py-2 sm:py-3"
              >
                <div className="w-2 h-2 rounded-full bg-[#F29A0C]" />
                <span className="text-[9px] sm:text-[10px] tracking-[3px] sm:tracking-[4px] uppercase font-semibold text-white">
                  WELLNESS TRANSFORMATION
                </span>
              </motion.div>
              <motion.h1
                variants={fadeUp}
                
                className="text-[34px] sm:text-[52px] md:text-[74px] lg:text-[72px] leading-[1.05] md:leading-[1.0] tracking-[-1px] font-semibold text-white mt-4"
                style={{ fontFamily: "Poppins", ...gpu }}>
                From Stress<br />& Disconnection<br />To
                <span className="text-[#F29A0C]"> Inner Peace</span>
              </motion.h1>
              <motion.p
                variants={fadeUp}
                style={gpu}
                className="text-white/85 text-sm sm:text-[18px] md:text-[20px] leading-[26px] sm:leading-[40px] mt-6 lg:mt-10 max-w-[760px]"
              >
                Experience a complete transformation of your physical health,
                emotional wellbeing, focus, mindfulness, and spiritual balance
                through authentic yoga guided by Divyyog Foundation.
              </motion.p>

              <motion.div variants={fadeUp} style={gpu} className="flex flex-wrap gap-5 mt-8">
                <motion.button
                  type="button"
                  onClick={handleStartJourney}
                  whileHover={{ y: -4, scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  style={gpu}
                  className="group relative overflow-hidden rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF7A00] text-white px-8 py-4 text-[15px] font-semibold shadow-[0_25px_80px_rgba(242,154,12,0.30)] cursor-pointer border-none"
                >
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-all duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" />
                  <span className="relative z-20 flex items-center gap-3">
                    Start Your Journey <FaArrowRight />
                  </span>
                </motion.button>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ══════════════════════════════════════════════════════
          SECTION 2   BEFORE / AFTER
      ══════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden">
        <div className="grid lg:grid-cols-2">

          {/* BEFORE   slides from left */}
          <motion.div
            variants={slideLeft}
            initial="hidden"
            whileInView="show"
            viewport={vp(0.18)}
            style={gpu}
            className="relative overflow-hidden min-h-[750px] bg-[#120700]"
          >
            <img
              src="https://images.pexels.com/photos/4101143/pexels-photo-4101143.jpeg"
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/70 to-[#120700]/90" />

            <div className="relative z-20 h-full flex items-center px-10 lg:px-20">
              <motion.div
                variants={stagger(0.1)}
                initial="hidden"
                whileInView="show"
                viewport={vp(0.2)}
              >
                <motion.div
                  variants={fadeUp} style={gpu}
                  className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#F29A0C]/10 border border-[#F29A0C]/20 mt-8"
                >
                  <div className="w-2 h-2 rounded-full bg-[#F29A0C]" />
                  <span className="text-red-400 uppercase tracking-[5px] text-xs font-semibold">WITHOUT WELLNESS</span>
                </motion.div>

                <motion.h2
                  variants={fadeUp} 
                  className="text-[48px] md:text-[64px] leading-[1.0] tracking-[-1px] font-semibold text-white mt-6"
                  style={{ fontFamily: "Poppins", ...gpu }}
                >
                  Stress.<br />Burnout.<br />Disconnection.
                </motion.h2>
                <motion.div variants={stagger(0.08)} className="space-y-6 mt-14">
                  {["Poor sleep and low energy", "Mental stress and anxiety", "Lack of focus and discipline", "Physical stiffness and fatigue"].map((item, i) => (
                    <motion.div key={i} variants={listItem} style={gpu} className="flex items-center gap-5">
                      <div className="w-3 h-3 rounded-full bg-red-400 flex-shrink-0" />
                      <span className="text-white/70 text-[17px]">{item}</span>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </motion.div>

          {/* AFTER   slides from right */}
          <motion.div
            variants={slideRight}
            initial="hidden"
            whileInView="show"
            viewport={vp(0.18)}
            style={gpu}
            className="relative overflow-hidden min-h-[750px]"
          >
            <img
              src="https://images.pexels.com/photos/3822906/pexels-photo-3822906.jpeg"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#F29A0C]/50 to-[#120700]/55" />

            <div className="relative z-20 h-full flex items-center px-10 lg:px-20">
              <motion.div
                variants={stagger(0.1)}
                initial="hidden"
                whileInView="show"
                viewport={vp(0.2)}
              >
                <motion.div
                  variants={fadeUp} style={gpu}
                  className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#ffffff]/10 border border-[#ffffff]/20 mt-8"
                >
                  <div className="w-2 h-2 rounded-full bg-[#F29A0C]" />
                  <span className="text-[#ffffff] tracking-[3px] uppercase text-xs font-semibold">WITH DIVYYOG FOUNDATION</span>
                </motion.div>

                <motion.h2
                  variants={fadeUp} 
                  className="text-[48px] md:text-[64px] leading-[1.0] tracking-[-1px] font-semibold text-white mt-6"
                  style={{ fontFamily: "Poppins", ...gpu }}
                >
                  Balance.<br />Confidence.<br />Inner Peace.
                </motion.h2>

                <motion.div variants={stagger(0.08)} className="space-y-6 mt-14">
                  {["Mindfulness and emotional calm", "Better posture and flexibility", "Improved sleep and energy", "Daily wellness discipline"].map((item, i) => (
                    <motion.div key={i} variants={listItem} style={gpu} className="flex items-center gap-5">
                      <div className="w-3 h-3 rounded-full bg-[#F29A0C] flex-shrink-0" />
                      <span className="text-white/85 text-[17px]">{item}</span>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </motion.div>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          SECTION 3   BENEFITS CARDS
      ══════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden py-16">

        {/* Static backgrounds   no animation */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFFDF8] via-[#FFF8EE] to-[#FFFFFF]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,154,12,0.10),transparent_30%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(242,154,12,0.08),transparent_30%)]" />

        {/* Glow   opacity-only animation */}
        <motion.div
          animate={{ opacity: [0.08, 0.16, 0.08] }}
          transition={{ repeat: Infinity, duration: 12, ease: "easeInOut" }}
          className="absolute top-[-220px] right-[-180px] w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.12)_0%,transparent_70%)] pointer-events-none will-change-opacity"
          style={gpu}
        />

        <div className="relative z-20 space-y-12">
          {benefitsCards.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={vp(0.1)}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              style={gpu}
              className="max-w-[1550px] mx-auto px-6 lg:px-20"
            >
              {/* CARD */}
              <motion.div
                whileHover={{ y: -8 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                style={gpu}
                className="group relative overflow-hidden rounded-[42px] border border-[#F4D9B5] bg-white/75 backdrop-blur-3xl shadow-[0_40px_140px_rgba(0,0,0,0.06)]"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white via-[#FFFDF8] to-[#FFF8EE]" />
                <div className="absolute top-[-100px] right-[-100px] w-[260px] h-[260px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.08)_0%,transparent_70%)] pointer-events-none" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-1000 bg-[linear-gradient(120deg,transparent,rgba(255,255,255,0.14),transparent)] -translate-x-full group-hover:translate-x-full" />

                <div className="relative z-20 grid lg:grid-cols-[1fr_320px] min-h-[300px]">

                  {/* CONTENT SIDE */}
                  <div className="relative flex items-center px-10 lg:px-16 py-4">
                    <span className="absolute top-6 right-8 text-[100px] leading-none font-semibold text-[#F29A0C] opacity-[0.18]">
                      0{index + 1}
                    </span>

                    {/* Stagger the inner content independently */}
                    <motion.div
                      className="max-w-[600px]"
                      variants={stagger(0.09)}
                      initial="hidden"
                      whileInView="show"
                      viewport={vp(0.1)}
                    >
                      <motion.div
                        variants={fadeUp} style={gpu}
                        className="inline-flex items-center gap-3 rounded-full border border-[#F4D9B5] bg-[#FFF8EE] px-5 py-3"
                      >
                        <div className="w-2 h-2 rounded-full bg-[#F29A0C]" />
                        <span className="text-[10px] tracking-[3px] uppercase font-semibold text-[#111111]">WELLNESS EXPERIENCE</span>
                      </motion.div>

                      <motion.div
                        variants={scaleUp}
                        whileHover={{ rotate: 8, scale: 1.05 }}
                        style={gpu}
                        className="mt-4 w-16 h-16 rounded-[28px] bg-gradient-to-br from-[#F29A0C] to-[#FF7A00] flex items-center justify-center text-white text-3xl shadow-[0_25px_80px_rgba(242,154,12,0.25)]"
                      >
                        {item.icon}
                      </motion.div>

                      <motion.h2
                        variants={fadeUp} 
                        className="text-[42px] md:text-[44px] leading-[0.94] tracking-[-1px] font-semibold text-[#111111] mt-4"
                        style={{ fontFamily: "Poppins", ...gpu }}
                      >
                        {item.title}
                      </motion.h2>

                      <motion.p variants={fadeUp} style={gpu} className="text-[#F29A0C] text-[13px] uppercase tracking-[3px] font-semibold mt-5">
                        {item.subtitle}
                      </motion.p>

                      <motion.p variants={fadeUp} style={gpu} className="text-[#555555] text-[16px] leading-[32px] mt-5">
                        {item.desc}
                      </motion.p>

                      <motion.div variants={stagger(0.07)} className="grid sm:grid-cols-2 gap-5 mt-5">
                        {["Live Guided Sessions", "Expert Yoga Mentors", "Mindful Wellness", "Global Community"].map((f, i) => (
                          <motion.div key={i} variants={listItem} style={gpu} whileHover={{ x: 4 }} className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-2xl bg-[#F29A0C]/10 flex items-center justify-center text-[#F29A0C] flex-shrink-0">
                              <FaCheckCircle />
                            </div>
                            <span className="text-[#222222] text-[14px] font-medium">{f}</span>
                          </motion.div>
                        ))}
                      </motion.div>

                      <motion.div
                        variants={scaleUp}
                        whileHover={{ scale: 1.03 }}
                        style={gpu}
                        className="inline-flex items-center gap-4 rounded-full border border-[#F4D9B5] bg-white px-6 py-4 mt-6 shadow-[0_10px_40px_rgba(0,0,0,0.04)]"
                      >
                        <FaUsers className="text-[#F29A0C]" />
                        <span className="text-[#111111] text-sm font-medium">{item.stats}</span>
                      </motion.div>
                    </motion.div>
                  </div>

                  {/* IMAGE SIDE */}
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={vp(0.1)}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
                    style={gpu}
                    className="relative overflow-hidden min-h-[540px]"
                  >
                    <motion.img
                      whileHover={{ scale: 1.06 }}
                      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                      src={item.image}
                      alt=""
                      style={gpu}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-l from-black/10 via-transparent to-transparent" />

                    <motion.div
                      animate={{ y: [0, -8, 0] }}
                      transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                      style={gpu}
                      className="absolute bottom-6 left-6 right-6 rounded-[26px] border border-white/20 bg-white/10 backdrop-blur-2xl p-5"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white/70 text-[10px] uppercase tracking-[3px]">DAILY TRANSFORMATION</p>
                          <h4 className="text-white text-[22px] font-semibold mt-2">Wellness Journey</h4>
                        </div>
                        <div className="w-14 h-14 rounded-[20px] bg-white/10 flex items-center justify-center text-white text-xl">
                          {item.icon}
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>

                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          SECTION 4   DAILY EXPERIENCE GRID
      ══════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden py-16">

        <div className="absolute inset-0 bg-[#FFFDF8]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,154,12,0.12),transparent_28%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(242,154,12,0.08),transparent_28%)]" />

        {/* Glow   opacity only */}
        <motion.div
          animate={{ opacity: [0.08, 0.16, 0.08] }}
          transition={{ repeat: Infinity, duration: 12, ease: "easeInOut" }}
          className="absolute top-[-260px] left-[-180px] w-[800px] h-[800px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.14)_0%,transparent_70%)] pointer-events-none will-change-opacity"
          style={gpu}
        />

        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay bg-[url('https://www.transparenttextures.com/patterns/asfalt-light.png')]" />

        <div className="relative z-20 max-w-[1650px] mx-auto px-6 lg:px-20">

          {/* HEADER */}
          <motion.div
            className="max-w-[950px] mx-auto text-center"
            variants={stagger(0.1)}
            initial="hidden"
            whileInView="show"
            viewport={vp(0.15)}
          >
            <motion.div
              variants={scaleUp} style={gpu}
              className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#F29A0C]/10 border border-[#F29A0C]/20"
            >
              <div className="w-2 h-2 rounded-full bg-[#F29A0C]" />
              <span className="text-[#F29A0C] tracking-[3px] uppercase text-xs font-semibold">DAILY WELLNESS EXPERIENCE</span>
            </motion.div>

            <motion.h2
              variants={fadeUp} 
              className="text-[48px] md:text-[72px] leading-[1.2] tracking-[-1px] font-semibold text-[#111111] mt-6"
              style={{ fontFamily: "Poppins", ...gpu }}
            >
              Designed For<br />A Better Everyday Life
            </motion.h2>

            <motion.p
              variants={fadeUp} style={gpu}
              className="text-[#666666] text-[18px] md:text-[20px] leading-[40px] mt-8 max-w-[760px] mx-auto"
            >
              Experience mindful rituals and wellness practices that transform
              your mornings, improve focus, restore balance, and elevate your everyday lifestyle.
            </motion.p>
          </motion.div>

          {/* GRID */}
          <div className="relative mt-16">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.08)_0%,transparent_70%)] pointer-events-none" />

            <div className="grid lg:grid-cols-2 gap-8 relative z-20">
              {[
                { title: "Wake Calm",      desc: "Start your mornings with mindful breathing and peaceful awareness.",      image: "/assets/image 16.jpeg" },
                { title: "Breathe Better", desc: "Improve emotional balance through guided pranayama practices.",           image: "/assets/image 9.jpeg"  },
                { title: "Move Freely",    desc: "Enhance flexibility, mobility, and body posture with yoga flow.",         image: "/assets/image 26.jpeg" },
                { title: "Think Clearly",  desc: "Build focus, mindfulness, and mental clarity through meditation.",        image: "/assets/image 22.jpeg" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -60 : 60 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={vp(0.18)}
                  transition={{
                    duration: 0.85,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  whileHover={{ y: -10, scale: 1.01 }}
                  style={gpu}
                  className="group relative"
                >
                  <div className="relative overflow-hidden rounded-[42px] border border-[#F4D9B5] bg-white/70 backdrop-blur-3xl shadow-[0_30px_120px_rgba(0,0,0,0.05)]">
                    <div className="absolute top-[-100px] right-[-100px] w-[240px] h-[240px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.1)_0%,transparent_70%)] pointer-events-none" />

                    <div className="relative h-[340px] overflow-hidden">
                      <motion.img
                        whileHover={{ scale: 1.08 }}
                        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                        src={item.image}
                        alt=""
                        style={gpu}
                        className="w-full h-full object-fill"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

                      {/* Floating top bar */}
                      <motion.div
                        animate={{ y: [0, -6, 0] }}
                        transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                        style={gpu}
                        className="absolute w-60 top-6 left-6 rounded-full border border-white/20 bg-white/10 backdrop-blur-2xl px-5 py-4 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-[#F29A0C]" />
                          <span className="text-white text-[10px] tracking-[3px] uppercase font-semibold">DAILY WELLNESS</span>
                        </div>
                        <span className="text-white/60 text-sm">0{index + 1}</span>
                      </motion.div>

                      {/* Floating bottom card */}
                      <motion.div
                        animate={{ y: [0, -8, 0] }}
                        transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                        style={gpu}
                        className="absolute left-6 right-6 bottom-2 rounded-[28px] border border-white/60 bg-white/10 backdrop-blur-2xl px-6 py-3"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3
                              className="text-white text-[24px] leading-none tracking-[2px] font-semibold"
                              style={{ fontFamily: "Poppins" }}
                            >
                              {item.title}
                            </h3>
                            <p className="text-white/70 text-sm mt-2 leading-[28px] max-w-[320px]">{item.desc}</p>
                          </div>
                          <motion.div
                            whileHover={{ scale: 1.12, rotate: 12 }}
                            style={gpu}
                            className="w-16 h-16 rounded-[22px] bg-gradient-to-br from-[#F29A0C] to-[#FF7A00] flex items-center justify-center text-white text-2xl shadow-[0_15px_50px_rgba(242,154,12,0.30)] flex-shrink-0"
                          >
                            <GiLotus />
                          </motion.div>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          SECTION 5   CTA BANNER
      ══════════════════════════════════════════════════════ */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={vp(0.12)}
        transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
        style={gpu}
        className="relative overflow-hidden rounded-tl-[40px] rounded-tr-[40px] overflow-hidden mt-12 border border-[#F4D9B5] bg-gradient-to-br from-[#FFF8EE] via-[#FFF3E0] to-[#FFE6C7] shadow-[0_40px_140px_rgba(242,154,12,0.12)]"
      >
        {/* Static background   no animation needed */}
        <div className="absolute inset-0 rounded-tl-[40px] rounded-tr-[40px] overflow-hidden">
          <img src="/assets/image 25.jpeg" alt="" className="w-full h-full rounded-tl-[40px] rounded-tr-[40px] overflow-hidden object-cover opacity-[0.48]" />
          <div className="absolute top-[-120px] right-[-120px] w-[420px] h-[420px] rounded-tl-[40px] rounded-tr-[40px] overflow-hidden bg-[radial-gradient(circle,rgba(242,154,12,0.15)_0%,transparent_70%)] pointer-events-none" />
          <div className="absolute bottom-[-140px] left-[-100px] w-[320px] h-[320px] rounded-tl-[40px] rounded-tr-[40px] overflow-hidden bg-[radial-gradient(circle,rgba(255,179,71,0.1)_0%,transparent_70%)] pointer-events-none" />
        </div>

        <div className="relative z-20 px-8 py-14 md:px-16 md:py-16">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-16 items-center">

            {/* LEFT */}
            <motion.div
              variants={stagger(0.09)}
              initial="hidden"
              whileInView="show"
              viewport={vp(0.15)}
            >
              <motion.div
                variants={scaleUp} style={gpu}
                className="inline-flex items-center gap-3 rounded-full border border-[#F4D9B5] bg-white/70 backdrop-blur-xl px-5 py-3"
              >
                <div className="w-2 h-2 rounded-full bg-[#F29A0C]" />
                <span className="text-[#F29A0C] text-[10px] tracking-[4px] uppercase font-semibold">WELLNESS EXPERIENCE</span>
              </motion.div>

              <motion.h2
                variants={fadeUp} 
                className="text-[42px] md:text-[76px] leading-[1.2] tracking-[-1px] font-semibold text-[#333333] mt-8 max-w-[760px]"
                style={{ fontFamily: "Poppins", ...gpu }}
              >
                Your Journey To<br />
                <span className="text-[#F29A0C]">Better Living</span>
              </motion.h2>

              <motion.p variants={fadeUp} style={gpu} className="text-[#111111] text-[18px] leading-[36px] mt-8 max-w-[700px]">
                Experience mindful wellness, guided yoga, healing meditation,
                and holistic transformation designed to elevate your daily lifestyle.
              </motion.p>
              <motion.div variants={fadeUp} style={gpu} className="flex flex-wrap items-center gap-5 mt-10">
                <motion.button
                  type="button"
                  onClick={handleStartJourney}
                  whileHover={{ y: -4, scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  style={gpu}
                  className="rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8A00] text-white px-9 py-4 text-[14px] font-semibold shadow-[0_20px_60px_rgba(242,154,12,0.25)] cursor-pointer border-none"
                >
                  Start Wellness Journey
                </motion.button>
                <Link to="/courses">
                  <motion.button
                    whileHover={{ y: -4 }}
                    style={gpu}
                    className="rounded-full border border-[#F4D9B5] bg-white/70 backdrop-blur-xl text-[#111111] px-9 py-4 text-[14px] font-semibold hover:bg-[#F29A0C] hover:text-white transition-all duration-500 cursor-pointer"
                  >
                    Explore Programs
                  </motion.button>
                </Link>
              </motion.div>
            </motion.div>

            {/* RIGHT   floating cards stagger in */}
            <div className="relative h-[420px] hidden lg:block">

              <motion.div
                initial={{ opacity: 0, x: 60, y: -20 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={vp(0.15)}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.08 }}
                whileHover={{ scale: 1.04 }}
                style={gpu}
                className="absolute top-0 left-0 w-[240px] rounded-[32px] border border-[#F4D9B5] bg-white/75 backdrop-blur-2xl p-6 shadow-[0_20px_60px_rgba(0,0,0,0.06)]"
              >
                <motion.div
                  animate={{ y: [0, -12, 0] }}
                  transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                  style={gpu}
                >
                  <div className="w-14 h-14 rounded-[22px] bg-gradient-to-br from-[#F29A0C] to-[#FF8A00] flex items-center justify-center text-white text-2xl shadow-md">
                    <GiMeditation size={28} />
                  </div>
                  <h4 className="text-[#111111] text-[22px] font-semibold mt-5">Mindfulness</h4>
                  <p className="text-[#777777] text-sm leading-[28px] mt-3">Create calmness and clarity through mindful practices.</p>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 60, y: 20 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={vp(0.15)}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.18 }}
                whileHover={{ scale: 1.04 }}
                style={gpu}
                className="absolute top-20 right-0 w-[240px] rounded-[32px] border border-[#F4D9B5] bg-gradient-to-br from-[#F29A0C] to-[#FF8A00] p-6 shadow-[0_30px_80px_rgba(242,154,12,0.25)] z-20"
              >
                <motion.div
                  animate={{ y: [0, 10, 0] }}
                  transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
                  style={gpu}
                >
                  <div className="w-14 h-14 rounded-[22px] bg-white/15 backdrop-blur-xl flex items-center justify-center text-white text-2xl shadow-md">
                    <FiZap size={26} />
                  </div>
                  <h4 className="text-white text-[22px] font-semibold mt-5">Energy</h4>
                  <p className="text-white/80 text-sm leading-[28px] mt-3">Restore inner energy and emotional balance daily.</p>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 40, y: 60 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={vp(0.15)}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.28 }}
                whileHover={{ scale: 1.04 }}
                style={gpu}
                className="absolute bottom-0 left-16 w-[260px] rounded-[32px] border border-[#F4D9B5] bg-white/75 backdrop-blur-2xl p-6 shadow-[0_20px_60px_rgba(0,0,0,0.06)]"
              >
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ repeat: Infinity, duration: 5.5, ease: "easeInOut" }}
                  style={gpu}
                >
                  <div className="w-14 h-14 rounded-[22px] bg-gradient-to-br from-[#F29A0C] to-[#FF8A00] flex items-center justify-center text-white text-2xl shadow-md">
                    <FaLeaf size={24} />
                  </div>
                  <h4 className="text-[#111111] text-[22px] font-semibold mt-5">Balance</h4>
                  <p className="text-[#777777] text-sm leading-[28px] mt-3">Build sustainable wellness habits for everyday life.</p>
                </motion.div>
              </motion.div>

            </div>
          </div>
        </div>
      </motion.div>

    </div>
  );
};

export default Benefits;